import { Card, Divider, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from '../../../../redux/store';
import { formCardStyle, scrollSX } from '../contractStyles';
import FormAutocomplete from './contract-form-fields/FormAutocomplete';
import FormCheckboxField from './contract-form-fields/FormCheckboxField';
import FormDatePickerField from './contract-form-fields/FormDatePickerField';
import FormTextField from './contract-form-fields/FormTextField';
import FormTitle from './contract-form-fields/FormTitle';
import SectionHeader from './contract-form-fields/SectionHeader';


ContractInfoCard.propTypes = {
    isEditable: PropTypes.bool
}
export default function ContractInfoCard({ isEditable = false }) {
    const { id } = useParams();
    const facility = useSelector((state) => state.contracts?.contractById?.[id]?.facility);
    const [focusedField, setFocusedField] = useState(null);
    const [open, setOpen] = useState(false);
    const { contractOptions: { facilities = [], contractTypes = [], contractVendors = [] } } = useSelector((state) => state.contracts || {});
    const { control, trigger, formState: { dirtyFields } } = useFormContext();
    const startDate = useWatch({ control, name: 'startDate', });
    const endDate = useWatch({ control, name: 'endDate', });

    useEffect(() => {
        if (dirtyFields.startDate && dirtyFields.endDate) {
            trigger('endDate')
            trigger('startDate')
        }
    }, [trigger, endDate, startDate, dirtyFields.startDate, dirtyFields.endDate])

    return (
        <Card sx={{
            ...formCardStyle,
            ...scrollSX,
            maxHeight: open ? 'none' : '280px',
        }}>
            <SectionHeader title="Contract Info" open={open} setOpen={setOpen} />
            <Divider sx={{ mb: 1, mt: 1, borderWidth: 1 }} />
            <Grid container spacing={1.5}>

                <Grid item xs={12} >
                    <FormTitle title='Facility' required />
                    <FormAutocomplete
                        disabled={!isEditable}
                        fieldName={'facilityId'}
                        options={facilities}
                        required
                        readOnlyValue={facility}
                    />
                </Grid>

                <Grid item xs={12} >
                    <FormTitle title='Vendor' required />
                    <FormAutocomplete
                        disabled={!isEditable}
                        fieldName={'vendorId'}
                        options={contractVendors}
                        required
                    />
                </Grid>

                <Grid item xs={12} >
                    <FormTitle title='Contract Type' required />
                    <FormAutocomplete
                        disabled={!isEditable}
                        fieldName={'contractTypeId'}
                        options={contractTypes.map(type => ({ value: type.id, label: type.name, }))}
                        required
                    />
                </Grid>

                <Grid item xs={12} >
                    <FormTitle title='Account Number' />
                    <FormTextField
                        disabled={!isEditable}
                        fieldName={'accountNumber'}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                    />
                </Grid>

                <Grid item xs={12} md={6} >
                    <FormTitle title='Start Date' required />
                    <FormDatePickerField
                        disabled={!isEditable}
                        fieldName={'startDate'}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                        validation={{ required: true, maxDate: endDate ? dayjs(endDate).toDate() : null }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormTitle title='End Date' required />
                    <FormDatePickerField
                        disabled={!isEditable}
                        fieldName={'endDate'}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                        validation={{ required: true, minDate: startDate ? dayjs(startDate).toDate() : null }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormTitle title='Renewal Date' />
                    <FormDatePickerField
                        disabled={!isEditable}
                        fieldName={'renewalDate'}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormTitle title='Auto Renew' />
                    <FormTextField
                        disabled={!isEditable}
                        fieldName={'autoRenew'}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                    />
                </Grid>

                <Grid item xs={12} >
                    <FormTitle title='Termination Effective Date' />
                    <FormDatePickerField
                        disabled={!isEditable}
                        fieldName={'terminationEffectiveDate'}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ mb: 1, mt: 1, borderWidth: 1 }} />
                </Grid>

                <Grid item xs={12} md={4} >
                    <FormCheckboxField
                        disabled={!isEditable}
                        fieldName={'previousOwnership'}
                        label='Previous Ownership'
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormCheckboxField
                        disabled={!isEditable}
                        fieldName={'assumed'}
                        label='Assumed'
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormCheckboxField
                        disabled={!isEditable}
                        fieldName={'isTerminated'}
                        label='Is Terminated'
                    />
                </Grid>

            </Grid>
        </Card >
    );
}
